import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import Drawer from '..';
import DrawerContent from '../content';
import DrawerHeader from '../header';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import {
  ClassifierCreateOption,
  ClassifierModel,
  ClassifierSubType,
  getTypedClassifier,
  IntentClassifierModel,
  RegexClassifierModel,
} from '@/lib/models/classifier';
import {
  getClassifierById,
  getClassifierUsages,
  createClassifier,
  patchClassifier,
  deleteClassifier,
} from '@/lib/services/classification.service';
import useQueryHelper from '@/hooks/use-query-helper';
import RouterTabs from '@/components/router-tabs';
import ClassifierDetails from './classifier-details';
import ClassifierPolicies from './policies';
import useToast from '@/hooks/use-toast.hook';
import PhraseSetForm from './intent/phrase-set';
import ClassifierTypeSelector from './classifier-type-selector';
import { getRegexPatternTemplateById } from './classifier-type-selector/helpers';
import CustomEntityForm from './custom-entity-form';
import { DrawerProps } from '../helpers';
import useViewMode from '@/hooks/use-view-mode.hook';
import MoreMenu from '@/components/more-menu';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useModal from '@/hooks/use-modal.hook';
import useFeature from '@/hooks/use-feature.hook';
import { Form } from '@/components/form';
import DrawerButtons from '@/components/form/drawer-buttons';
import { FieldError } from '@/lib/services';
import { TabPanel } from '@/components/router-tabs/helpers';
import IntentForm from './intent-form';

interface Props extends DrawerProps {
  id: string;
}

export const ClassifierDrawer: FC<Props> = ({
  open,
  onClose,
  className,
  id,
  isTopDrawer,
  hideUpdateMenu,
}) => {
  const { toast, errorToast } = useToast();
  const { openModal } = useModal();
  const { canChangeClassifiers, hasClassifiers } = useFeature();
  const [classifierId, setClassifierId] = useState(id || null);
  const isNew = !classifierId;
  const { setMode, inWriteMode } = useViewMode(isNew ? 'add' : 'view');
  const [selectedClassifierType, setSelectedClassifierType] =
    useState<ClassifierCreateOption | null>(null);
  const [regexTemplateId, setRegexTemplateId] = useState<string | null>(null);
  const hasRegexTemplateId = !!regexTemplateId;

  const query = useQuery(
    [QueryKey.ClassifierView, classifierId],
    async () => getClassifierById(classifierId!),
    { enabled: !isNew }
  );
  const { showLoader: classifiersLoading } = useQueryHelper(query);
  const { data, refetch } = query;
  const classifier = data || new ClassifierModel();
  const { name: classifierName, subtype: classifierSubtype } = classifier;

  const compClassifierType = isNew ? selectedClassifierType || 'intent' : classifierSubtype;
  const isRegexClassifier = compClassifierType === 'regex';
  const showTypeSelector = isNew && hasClassifiers && !selectedClassifierType;

  const usageQuery = useQuery([QueryKey.ClassifierUsage], async () => getClassifierUsages());
  const { data: classifierUsageMap } = usageQuery;
  const { showLoader: usageLoading } = useQueryHelper(usageQuery);

  const classifierUsage = useMemo(() => {
    return (
      classifierUsageMap?.get(classifierId || '') || {
        policies: [],
        inUse: false,
      }
    );
  }, [classifierId, classifierUsageMap]);

  const handleRefresh = () => {
    refetchQuery([QueryKey.ClassifiersDataset, QueryKey.ClassifiersList, QueryKey.ClassifierUsage]);
  };

  const formModel = useMemo(
    () =>
      getTypedClassifier(
        new ClassifierModel({
          enabled: true,
          subtype: isRegexClassifier ? ClassifierSubType.Regex : ClassifierSubType.Custom,
        })
      ),
    [isRegexClassifier]
  );

  const formHandle = useFormHandle(
    {
      initialValues: formModel,
      validateOnChange: !isNew,
      onSubmit: async (classifier, { resetForm, setFieldError }) => {
        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
        };

        if (!classifierId) {
          const { fieldErrors, error, created, model } = await createClassifier(classifier);

          if (fieldErrors) {
            setFieldErrors(fieldErrors);
            return;
          }

          if (error || !created) {
            errorToast(error || 'The classifier was not created');
            return;
          }

          resetForm({ values: classifier });
          toast(
            isRegexClassifier ? 'The custom entity was created' : 'The intent topic was created'
          );
          setClassifierId(model?.id || '');
          handleRefresh();
          setMode('view');
          return;
        }

        const { patched, fieldErrors, error } = await patchClassifier(classifier);

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (error || !patched) {
          errorToast(error || 'The intent topic was not updated');
          return;
        }

        resetForm({ values: classifier });
        toast('The intent topic was updated');
        refetch();
        handleRefresh();
        setMode('view');
      },
    },
    { dirtyLockDisabled: true }
  );
  const { handleSubmit, canSubmit, isSubmitting, loadData } = formHandle;

  useEffect(() => {
    if (!isNew || !selectedClassifierType) {
      return;
    }
    if (selectedClassifierType === 'regex' && hasRegexTemplateId) {
      const template = getRegexPatternTemplateById(regexTemplateId);
      if (template) {
        const { title, description, regexPattern } = template;
        loadData(
          new RegexClassifierModel({
            name: title,
            description,
            config: { pattern: regexPattern },
          })
        );
        return;
      }
    }
    loadData(formModel);
  }, [isNew, selectedClassifierType, regexTemplateId, formModel, loadData, hasRegexTemplateId]);

  const handleClassifierTypeSelect = (type: ClassifierCreateOption) => {
    setRegexTemplateId(null);
    setSelectedClassifierType(type);
  };

  const handleRegexTemplateSelect = (templateId: string) => {
    setRegexTemplateId(templateId);
    setSelectedClassifierType('regex');
  };

  const handleCancel = () => {
    if (isNew) {
      if (hasClassifiers && selectedClassifierType) {
        setSelectedClassifierType(null);
        setRegexTemplateId(null);
        return;
      }
      onClose(true);
      return;
    }
    setMode('view');
    loadData(classifier || new ClassifierModel());
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: <>Are you sure you want to delete {classifierName}?</>,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deleteClassifier(classifierId!).then(({ deleted, error }) => {
            if (!deleted) {
              errorToast(error || 'The intent topic was not deleted');
              return;
            }

            setClassifierId('');
            onClose(true);
            toast('The intent topic was deleted');
            handleRefresh();
          });
        }
      },
    });
  };

  const handleUpload = useCallback(() => {
    toast('Example messages uploaded successfully');
    refetch();
  }, [refetch, toast]);

  const initialTestPatterns = useMemo(() => {
    if (hasRegexTemplateId) {
      return getRegexPatternTemplateById(regexTemplateId)?.sampleTestText;
    }
    return undefined;
  }, [hasRegexTemplateId, regexTemplateId]);

  const isLoading = classifiersLoading || usageLoading;
  const { inUse, policies } = classifierUsage;

  const showUpdateMenu = !isNew && canChangeClassifiers;
  const canDelete = !inUse;
  const deleteSubtitle = inUse ? 'You cannot delete an intent topic that is in use' : '';

  const panels: TabPanel[] = [
    {
      label: 'Profile',
      icon: 'info-circle',
      path: 'profile',
      component: <ClassifierDetails classifier={classifier} />,
    },
  ];
  if (classifier instanceof IntentClassifierModel) {
    panels.push({
      label: 'Example Messages',
      icon: 'text-file',
      path: 'example-messages',
      component: <PhraseSetForm classifier={classifier} onUpload={handleUpload} />,
      disabled: isNew,
    });
  }
  panels.push({
    label: 'Policies',
    icon: 'policy',
    path: 'policies',
    component: <ClassifierPolicies policies={policies} />,
    disabled: isNew,
  });

  const drawerTitle = useMemo(() => {
    if (showTypeSelector) {
      return 'Create a Classifier';
    }
    if (isNew) {
      return isRegexClassifier ? 'Create a Custom Entity' : 'Create an Intent Topic';
    }
    return classifierName;
  }, [showTypeSelector, isNew, classifierName, isRegexClassifier]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className={className}
      query={query}
      loading={isLoading}
      size="large"
    >
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={
          <MoreMenu menuId="classifier-menu">
            {showUpdateMenu && !hideUpdateMenu && (
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={!inWriteMode}
                canDelete={canDelete && !inWriteMode}
                deleteSubtitle={deleteSubtitle}
              />
            )}
          </MoreMenu>
        }
      >
        {drawerTitle}
      </DrawerHeader>
      <DrawerContent>
        {!inWriteMode ? (
          <RouterTabs variant="drawer" panels={panels} />
        ) : (
          <Form formHandle={formHandle} data={isNew ? undefined : classifier}>
            {showTypeSelector ? (
              <ClassifierTypeSelector
                onSelect={handleClassifierTypeSelect}
                onSelectRegexTemplate={handleRegexTemplateSelect}
              />
            ) : (
              <>
                {isRegexClassifier ? (
                  <CustomEntityForm
                    formHandle={formHandle}
                    readonly={!canChangeClassifiers}
                    initialTestPatterns={initialTestPatterns}
                  />
                ) : (
                  <IntentForm formHandle={formHandle} readonly={!canChangeClassifiers} />
                )}
                <DrawerButtons
                  submit
                  cancel
                  isNew={isNew}
                  canSubmit={canSubmit}
                  canCancel={!isSubmitting}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  formHandle={formHandle}
                />
              </>
            )}
          </Form>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default ClassifierDrawer;
